import React, { useRef, useState } from "react"

import * as S from "./styles"
import { useTranslation, useValidation } from "hooks"
import { translations } from "./translations"
import { Modal, Alert } from "components/shared"
import * as Yup from "yup"
import { Form } from "@unform/web"
import { FormHandles } from "@unform/core"
import { useMutation, useQuery } from "react-query"
import { Select } from "components/shared/Form"
import { apiGeneral, notify } from "services"

interface Props {
  commercialTeamId: string
  title: string
  roleId: number
}

const TeamRelations: React.FC<Props> = ({ commercialTeamId, roleId, title }) => {
  const [modalInfo, setModalInfo] = useState({ isOpen: false, id: null })
  const formCodeRef = useRef<FormHandles>(null)
  const { getTranslation } = useTranslation(translations)
  const { handleFormErrors } = useValidation()

  const getQueryRoleId = (roleId: number) => {
    switch (roleId) {
      case 6:
        return 9
      case 4:
        return 6
      default:
        return 9
    }
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: [`get-commercial-team-relations-${commercialTeamId}`],
    queryFn: async () => {
      const { data } = await apiGeneral.get("/comercial-team-relations?superior_commercial_team_id=" + commercialTeamId)

      return data
    },
  })

  const { data: commercialTeam, isLoading: isLoadingCommercialTeam } = useQuery(
    {
      queryKey: [`get-commercial-team-options-${commercialTeamId}-${roleId}`],
      queryFn: async () => {
        const { data } = await apiGeneral.get("/comercial-team", {
          params: {
            role_id: getQueryRoleId(roleId),
            limit: 1000,
          },
        })

        return data
      },
      select: (data: any) => {
        return data?.data.map((d: any) => ({ label: d.name, value: d.id }))
      },
    }
  )

  const { mutate, isLoading: isPending } = useMutation({
    mutationFn: async (data: any) => {
      await apiGeneral.post("/comercial-team-relations", {
        ...data,
        superior_commercial_team_id: Number(commercialTeamId),
      })
    },
  })

  const handleSubmit = async (data: any) => {
    try {
      const schema = Yup.object().shape({
        subordinate_commercial_team_id: Yup.string().required(),
      })

      await schema.validate(data, {
        abortEarly: false,
      })

      mutate(data, {
        onSuccess: () => {
          formCodeRef.current?.clearField("subordinate_commercial_team_id")
          refetch()
          notify("success", "Equipe comercial adicionada com sucesso!")
        },
        onError: (err) => {
          notify("error", "Oops! Não foi possível adicionar equipe comercial!")
        },
      })
    } catch (error) {
      handleFormErrors(error, formCodeRef)
    }
  }

  const { mutate: mutateDelete, isLoading: isPendingDelete } = useMutation({
    mutationFn: async (id: any) => {
      await apiGeneral.delete(`/comercial-team-relations/${id}`)
    },
  })
  const handleDelete = () => {
    if (!modalInfo.id) return

    mutateDelete(modalInfo.id, {
      onSuccess: () => {
        refetch()
        setModalInfo({ isOpen: false, id: null })
        notify("success", "Equipe comercial removida com sucesso!")
      },
      onError: () => {
        notify("error", "Oops! Não foi possível remover equipe comercial.")
      },
    })
  }

  return (
    <S.Container>
      <Modal isOpen={modalInfo.isOpen}>
        <Alert
          title={`Remover Equipe Comercial`}
          text="Tem certeza que deseja realmente remover?"
          close={() => {
            setModalInfo({ isOpen: false, id: null })
          }}
          action={handleDelete}
          labelAction="Remover"
          isLoading={isPendingDelete}
        />
      </Modal>
      <S.Title>
        {title} {isLoading && <S.Loading />}
      </S.Title>
      <S.BoxContainer>
        <Form
          ref={formCodeRef}
          onSubmit={handleSubmit}
          placeholder=""
          onPointerEnterCapture={() => { }}
          onPointerLeaveCapture={() => { }}
        >
          <S.FormRow>
            <Select
              name="subordinate_commercial_team_id"
              label={getTranslation("commercialTeam")}
              isLoading={isLoadingCommercialTeam}
              isDisabled={isLoadingCommercialTeam}
              options={commercialTeam ?? []}
              placeholder={getTranslation("selecione")}
            />
            <S.ButtonMini
              type="button"
              className="add"
              onClick={() => formCodeRef.current?.submitForm()}
            >
              {isPending ? <S.Loading /> : getTranslation("adicionar")}
            </S.ButtonMini>
          </S.FormRow>
        </Form>
        {data?.map((code: any) => (
          <S.Item key={code.id}>
            <S.ItemLabel>
              <span>{getTranslation("timeComercial")}: </span>
              {code.subordinate.name}
            </S.ItemLabel>
            <S.DeleteButton
              onClick={() => setModalInfo({ isOpen: true, id: code.id })}
              type="button"
            >
              <S.Trash size={22} />
            </S.DeleteButton>
          </S.Item>
        ))}
      </S.BoxContainer>
    </S.Container>
  )
}

export default TeamRelations
